import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import type {
  SavedArtifactDisplay,
  SavedArtifactProvenance,
} from '../../../src/artifacts/saved-view-contract.mjs';
import type { SavedArtifactView } from '../../../src/artifacts/saved-view-contract.mjs';
import type { ExportFormat } from '../../../src/artifacts/export/index.mjs';
import artifactContentCopy from '@/i18n/ns/artifact-content';
import type { ExportCommit } from '../../../src/artifacts/distribution/types.mjs';
import type { ExportPanelData } from './view-models/panels';
import { artifactApi, artifactAudienceWriter } from './api';
import { ExportPanel } from '../artifact-panels/export-panel';
import { CompanyRequestError } from '@/company/api';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import readyCopy from '@/i18n/ns/ready';
import appPageCopy from '@/i18n/ns/app-page';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import panelsCopy from '@/i18n/ns/artifact-panels';
import speakerCopy from '@/i18n/ns/meetings-speakers';
import inspectCopy from '@/i18n/ns/inspect';
import type { ArtifactCanvasRenderer } from './outlet';
import type { ArtifactCanvasTarget, DraftCanvasTarget } from './routes';
import { LoadingState, ErrorState } from '../primitives';
import { SavedCanvasError, SavedCanvasLoading } from '../apps/app-opening';
import {
  useSavedArtifactCanvas,
  useSavedArtifactHistory,
  openNativeArtifactEditor,
} from './saved-canvas';
import { SavedVersionsSlotContext } from '../apps/saved-versions-slot';
import { VersionsPanel } from '../artifact-panels/versions-panel';
import { savedArtifactCanvasTarget } from './routes';
import { useExportOriginal } from './export-original';
import { ShareChip } from '../library/share-chip';
import { AppSharingChip } from '../apps/sharing-chip';
import { ViewerShareButton } from '../share-sheet/runs-on';
import { CanvasHeaderTools } from '../canvas-footer';
import { useOptionalWorkspace } from '../context';
import { CanvasSettledContext } from './canvas-settle';
import { hasBootstrap } from '../apps/runtime/runtime-control';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import type { SelectedAppHostInput } from '../apps/app-saved-display';

const viewTitle = (view: SavedArtifactView) =>
  view.display.kind === 'app' ? view.display.app.name : view.display.title;

/**
 * Apps use their native prepare-confirm command; other saved Artifacts retain their audience writer.
 * A viewer presses the same Share button and sees, read-only, what the work runs on.
 */
function SavedShare({ view, refresh }: { view: SavedArtifactView; refresh?: () => void }) {
  const workspace = useOptionalWorkspace();
  if (view.purpose === 'viewer')
    return (
      <CanvasHeaderTools>
        <ViewerShareButton
          kind="result"
          id={view.artifactId}
          title={viewTitle(view)}
          thing={view.display.kind === 'app' ? 'theApp' : 'theResult'}
          ownerName={view.display.kind === 'app' ? view.display.app.ownerName : undefined}
          selfId={workspace?.user?.id}
        />
      </CanvasHeaderTools>
    );
  if (view.display.kind === 'app')
    return (
      <AppSharingChip
        refreshKey={view}
        title={view.display.app.name}
        onChanged={refresh}
        selection={{
          artifactId: view.artifactId,
          versionId: view.versionId,
          revisionId: view.revisionId,
        }}
      />
    );
  return <SavedArtifactShare view={view} />;
}

function SavedArtifactShare({ view }: { view: SavedArtifactView }) {
  const workspace = useOptionalWorkspace();
  const saved = useMemo(
    () => ({
      artifactId: view.artifactId,
      versionId: view.versionId,
      revisionId: view.revisionId,
      versionNumber: view.number,
      // Mounted for an owner's editor view only (the call site checks it).
      purpose: 'editor' as const,
    }),
    [view.artifactId, view.versionId, view.revisionId, view.number],
  );
  const writer = useMemo(() => artifactAudienceWriter(saved), [saved]);
  return (
    <ShareChip
      itemKey={view.versionId}
      saved={saved}
      writer={writer}
      companyName={workspace?.companyName ?? ''}
      title={viewTitle(view)}
      resultId={view.artifactId}
    />
  );
}

// The latest a saved App's Share chip waits for the App to show before reading its audience.
const SHARE_AFTER_MS = 6000;

/** Viewer-owned markup accepts the public display directly. It never receives
 * the internal C9 commit/payload/binding and cannot select a newer version. */
export function createSavedArtifactCanvasRenderer(
  Display: ComponentType<{
    display: SavedArtifactDisplay;
    /** The saved ordinal of THIS version (never the current head). */
    number: number;
    /** The selected saved identity, for a display that binds host callbacks to it. */
    target: ArtifactCanvasTarget | DraftCanvasTarget;
    companyId: string;
    actorId: string;
    provenance?: SavedArtifactProvenance;
    actions?: ReactNode;
    onSharingChanged?: () => void;
  }>,
  AppDisplay?: ComponentType<SelectedAppHostInput>,
): ArtifactCanvasRenderer {
  return function SavedArtifactCanvasBody({ target, companyId, actorId, openVersion, openEditor }) {
    const locale = useChatInterfaceLocale();
    const session = useSessionBoundFetch();
    const openingApp = useMemo(
      () =>
        target.kind === 'artifact' &&
        hasBootstrap(session, {
          companyId,
          actorId,
          artifactId: target.id,
          revisionId: target.revisionId,
          versionId: target.versionId,
          purpose: target.purpose,
        }),
      [session, companyId, actorId, target.id, target.revisionId, target.versionId, target.purpose],
    );
    const state = useSavedArtifactCanvas(target, companyId, actorId);
    const [showHistory, setShowHistory] = useState(false);
    // Saved items other than an App have two tabs: the item itself (Slides, Document, Sheet, Chart) and its Versions.
    const [tab, setTab] = useState<'content' | 'versions'>('content');
    const versionsOn = showHistory || tab === 'versions';
    const history = useSavedArtifactHistory(
      target,
      companyId,
      actorId,
      versionsOn && state.status === 'ready',
    );
    const ready = useNamespaceCopy(readyCopy);
    const pageCopy = useNamespaceCopy(appPageCopy);
    const copy = useNamespaceCopy(artifactPanelsCopy);
    // A saved App shows its runtime first; the Share chip reads its audience once the App has shown
    // (or said it cannot), or after a short wait, so that read never competes with the App's opening.
    const viewKey = target.kind === 'artifact' ? target.versionId : null;
    const [settledFor, setSettledFor] = useState<string | null>(null);
    const settle = useCallback(() => setSettledFor(viewKey), [viewKey]);
    useEffect(() => {
      if (viewKey === null) return;
      const timer = setTimeout(settle, SHARE_AFTER_MS);
      return () => clearTimeout(timer);
    }, [viewKey, settle]);
    const versionsPanel = versionsOn ? (
      <VersionsPanel
        vm={
          history.history ?? {
            load: history.load,
            versions: [],
            canRestore: false,
            restore: { status: 'idle' },
          }
        }
        selectedVersionId={target.kind === 'artifact' ? target.versionId : undefined}
        onRetry={history.retry}
        onOpen={
          openVersion && history.history
            ? (row) => {
                const original = history.history?.versions.find(
                  (version) => version.versionId === row.versionId,
                );
                if (!original) return;
                const selected = savedArtifactCanvasTarget({
                  artifactId: target.id,
                  versionId: original.versionId,
                  revisionId: original.revisionId,
                  versionNumber: original.number,
                  purpose: history.history!.purpose,
                });
                if (selected) openVersion(selected);
              }
            : undefined
        }
      />
    ) : null;
    // The selected App's ticket is independently admitted by C14. Mount its
    // frame now; metadata and controls remain absent until the saved view answers.
    // Keep the same component/iframe when that view arrives, and retire on refusal.
    if (
      AppDisplay &&
      openingApp &&
      (state.status === 'loading' ||
        (state.status === 'ready' && state.view.display.kind === 'app'))
    ) {
      const view = state.status === 'ready' ? state.view : null;
      return (
        <div className="ap-saved-chrome">
          {view && (view.purpose === 'viewer' || settledFor === view.versionId) && (
            <SavedShare
              view={view}
              refresh={state.status === 'ready' ? state.refresh : undefined}
            />
          )}
          <CanvasSettledContext.Provider value={settle}>
            <SavedVersionsSlotContext.Provider
              value={{ wanted: setShowHistory, panel: versionsPanel }}
            >
              <AppDisplay
                display={view?.display.kind === 'app' ? view.display : null}
                target={target}
                companyId={companyId}
                actorId={actorId}
                onSharingChanged={state.status === 'ready' ? state.refresh : undefined}
              />
            </SavedVersionsSlotContext.Provider>
          </CanvasSettledContext.Provider>
        </div>
      );
    }
    if (state.status === 'loading') return <SavedCanvasLoading target={target} />;
    if (state.status === 'error')
      return <SavedCanvasError target={target} message={state.message} onRetry={state.retry} />;
    const edit =
      openEditor &&
      state.view.purpose === 'editor' &&
      ['document', 'report'].includes(state.view.display.kind) ? (
        <SavedEditButton
          key={JSON.stringify([
            companyId,
            actorId,
            state.view.artifactId,
            state.view.versionId,
            state.view.revisionId,
            state.view.number,
          ])}
          view={state.view}
          onOpen={openEditor}
        />
      ) : null;
    const isApp = state.view.display.kind === 'app';
    const displayKind = state.view.display.kind as string;
    const kindWord =
      displayKind === 'deck'
        ? copy.kindDeck
        : displayKind === 'report'
          ? copy.kindReport
          : displayKind === 'spreadsheet'
            ? copy.kindSpreadsheet
            : displayKind === 'chart'
              ? copy.kindChart
              : displayKind === 'table'
                ? copy.kindTable
                : copy.kindDocument;
    // No empty tabs: the first tab is named for what the item is, never a generic "Artifact".
    const contentLabel =
      displayKind === 'deck'
        ? ready.tabSlides
        : displayKind === 'report' || displayKind === 'document'
          ? ready.tabDocument
          : kindWord;
    const display = (
      <Display
        display={state.view.display}
        number={state.view.number}
        target={target}
        companyId={companyId}
        actorId={actorId}
        provenance={state.view.provenance}
        actions={edit}
        onSharingChanged={state.refresh}
      />
    );
    return (
      <div className="ap-saved-chrome">
        {(state.view.purpose === 'viewer' || !isApp || settledFor === state.view.versionId) && (
          <SavedShare view={state.view} refresh={state.refresh} />
        )}
        {!isApp && (
          <div className="ap-saved-tabs" role="tablist" aria-label={kindWord}>
            {(['content', 'versions'] as const).map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`ap-saved-tab-${id}`}
                data-testid={`saved-tab-${id}`}
                aria-selected={tab === id}
                aria-controls={`ap-saved-panel-${id}`}
                tabIndex={tab === id ? 0 : -1}
                onClick={() => setTab(id)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                    event.preventDefault();
                    const next = id === 'content' ? 'versions' : 'content';
                    setTab(next);
                    document.getElementById(`ap-saved-tab-${next}`)?.focus();
                  }
                }}
              >
                {id === 'content' ? contentLabel : pageCopy.tabVersions}
              </button>
            ))}
          </div>
        )}
        {isApp ? (
          <CanvasSettledContext.Provider value={settle}>
            {/* The page menu borrows the same Versions reader and open-version path. */}
            <SavedVersionsSlotContext.Provider
              value={{ wanted: setShowHistory, panel: versionsPanel }}
            >
              {display}
            </SavedVersionsSlotContext.Provider>
          </CanvasSettledContext.Provider>
        ) : tab === 'versions' ? (
          <div
            className="ap-saved-versions"
            role="tabpanel"
            id="ap-saved-panel-versions"
            aria-labelledby="ap-saved-tab-versions"
          >
            <p className="ap-saved-help">{formatCopy(ready.versionsHelp, { kind: kindWord })}</p>
            {versionsPanel}
          </div>
        ) : (
          <>
            <div role="tabpanel" id="ap-saved-panel-content" aria-labelledby="ap-saved-tab-content">
              <CanvasSettledContext.Provider value={settle}>
                {display}
              </CanvasSettledContext.Provider>
            </div>
            <div className="ap-saved-footer">
              {companyId &&
                actorId &&
                ['document', 'report', 'table', 'spreadsheet', 'deck'].includes(
                  state.view.display.kind,
                ) &&
                state.view.purpose === 'editor' && (
                  <SavedDocumentExports
                    key={JSON.stringify([
                      companyId,
                      actorId,
                      state.view.versionId,
                      state.view.revisionId,
                      state.view.number,
                      state.view.purpose,
                      locale,
                    ])}
                    view={state.view}
                    companyId={companyId}
                    actorId={actorId}
                  />
                )}
            </div>
          </>
        )}
      </div>
    );
  };
}

function SavedDocumentExports(props: {
  view: SavedArtifactView;
  companyId: string;
  actorId: string;
}) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const content = useNamespaceCopy(artifactContentCopy);
  const [opened, setOpened] = useState(false);
  const spreadsheet = props.view.display.kind === 'spreadsheet';
  const [format, setFormat] = useState<'xlsx' | 'csv' | 'docx' | 'pdf'>(
    spreadsheet ? 'xlsx' : 'docx',
  );
  const [sheetId, setSheetId] = useState(
    props.view.display.kind === 'spreadsheet' ? props.view.display.sheets[0]?.id : undefined,
  );
  if (props.view.display.kind === 'table') return <SavedFormatExports {...props} format="pdf" />;
  if (String(props.view.display.kind) === 'deck')
    return <SavedFormatExports {...props} format="pptx" />;
  return (
    <>
      <button
        className="ap-saved-action"
        type="button"
        aria-expanded={opened}
        onClick={() => setOpened((old) => !old)}
      >
        {copy.exportTitle}
      </button>
      {opened && (
        <>
          <label className="ap-option-row">
            {copy.exportTitle}
            <select
              value={format}
              onChange={(event) => {
                const selected = event.currentTarget.value;
                if (
                  selected === 'xlsx' ||
                  selected === 'csv' ||
                  selected === 'docx' ||
                  selected === 'pdf'
                )
                  setFormat(selected);
              }}
            >
              {spreadsheet ? (
                <>
                  <option value="xlsx">{copy.exportFormatXlsx}</option>
                  <option value="csv">{copy.exportFormatCsv}</option>
                </>
              ) : (
                <>
                  <option value="docx">{copy.exportFormatDocx}</option>
                  <option value="pdf">{copy.exportFormatPdf}</option>
                </>
              )}
            </select>
          </label>
          {props.view.display.kind === 'spreadsheet' && format === 'csv' && (
            <label className="ap-option-row">
              {content.sheetTabs}
              <select value={sheetId} onChange={(event) => setSheetId(event.currentTarget.value)}>
                {props.view.display.sheets.map((sheet) => (
                  <option key={sheet.id} value={sheet.id}>
                    {sheet.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <SavedFormatExports
            key={JSON.stringify([format, format === 'csv' ? sheetId : null])}
            {...props}
            format={format}
            sheetId={format === 'csv' ? sheetId : undefined}
            embedded
          />
        </>
      )}
    </>
  );
}

function SavedFormatExports({
  view,
  companyId,
  actorId,
  format,
  sheetId,
  embedded = false,
}: {
  view: SavedArtifactView;
  companyId: string;
  actorId: string;
  format: ExportFormat;
  sheetId?: string;
  embedded?: boolean;
}) {
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const [opened, setOpened] = useState(embedded);
  const [vm, setVM] = useState<ExportPanelData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const alive = useRef(true);
  const busy = useRef(false);
  const leaseRequested = useRef(false);
  const expiry = useRef<number | null>(null);
  const exportOriginal = useExportOriginal({
    companyId,
    actorId,
    artifactId: view.artifactId,
    revisionId: view.revisionId,
    versionId: view.versionId,
    number: view.number,
    purpose: 'editor',
    format,
    locale,
    sheetId,
  });
  const pending = useRef(exportOriginal.recovering);
  const recoveryStarted = useRef(false);
  const request = useMemo(
    () => ({
      artifactId: view.artifactId,
      revisionId: view.revisionId,
      versionId: view.versionId,
      number: view.number,
      purpose: 'editor' as const,
      format,
      ...(sheetId === undefined ? {} : { sheetId }),
      ...exportOriginal.original,
    }),
    [
      view.artifactId,
      view.revisionId,
      view.versionId,
      view.number,
      format,
      sheetId,
      exportOriginal.original.operationId,
      exportOriginal.original.manifestId,
    ],
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (expiry.current !== null) window.clearTimeout(expiry.current);
    };
  }, []);
  const deliver = useCallback(
    async (commit: ExportCommit, sent: typeof request) => {
      if (commit.versionId !== view.versionId || commit.manifestId !== sent.manifestId)
        throw new CompanyRequestError(409, 'export_binding_mismatch');
      const manifest = await artifactApi.readExportManifest(view.artifactId, sent);
      if (
        manifest.versionId !== view.versionId ||
        manifest.artifactId !== view.artifactId ||
        manifest.digest !== commit.manifestDigest
      )
        throw new CompanyRequestError(409, 'export_binding_mismatch');
      const primary = manifest.files.find((file) => file.role === 'primary');
      const about = manifest.files.find((file) => file.role === 'provenance');
      if (!primary || !about || manifest.files.length !== 2)
        throw new CompanyRequestError(409, 'export_binding_mismatch');
      if (leaseRequested.current) throw new CompanyRequestError(409, 'download_unavailable');
      leaseRequested.current = true;
      const lease = await artifactApi.createExportDownloadLease(view.artifactId, {
        versionId: view.versionId,
        manifestId: commit.manifestId,
        fileIds: [primary.id, about.id],
        purpose: 'editor-export',
      });
      if (
        lease.files.length !== 2 ||
        lease.files[0].id !== primary.id ||
        lease.files[1].id !== about.id
      )
        throw new CompanyRequestError(409, 'export_binding_mismatch');
      const options = await artifactApi.readExportOptions(sent, locale);
      const ready = {
        status: 'ready' as const,
        format,
        sizeBytes: primary.byteLength,
        version: view.number,
        files: lease.files,
      };
      const remaining = Date.parse(lease.expiresAt) - Date.now();
      if (!Number.isFinite(remaining) || remaining <= 0)
        throw new CompanyRequestError(409, 'download_unavailable');
      if (alive.current)
        setVM({
          ...options,
          formats: options.formats.filter((row) => row.id === format),
          job: ready,
        });
      expiry.current = window.setTimeout(() => {
        if (alive.current)
          setVM(
            (old) =>
              old && {
                ...old,
                job: {
                  status: 'failed',
                  format,
                  reason: 'stale',
                  retryable: false,
                },
              },
          );
      }, remaining);
    },
    [view.artifactId, view.versionId, view.number, locale, format],
  );
  const exportOrRecover = useCallback(
    async (recover: boolean) => {
      if (busy.current) return;
      busy.current = true;
      setVM((old) => old && { ...old, job: { status: 'running', format, progress: null } });
      try {
        // Retry is original-only. Null never becomes another POST/write/job.
        const lookupOnly = recover || pending.current || exportOriginal.recovering;
        const sent = lookupOnly ? request : { ...request, ...exportOriginal.begin() };
        if (!lookupOnly) {
          pending.current = true;
          leaseRequested.current = false;
          if (expiry.current !== null) window.clearTimeout(expiry.current);
        }
        const commit = lookupOnly
          ? await artifactApi.lookupExportReceipt(sent)
          : await artifactApi.exportArtifact(sent, locale);
        if (!commit) throw new CompanyRequestError(503, 'receipt_reconciliation_required');
        await deliver(commit, sent);
        pending.current = false;
        exportOriginal.resolved();
      } catch (error) {
        if (alive.current)
          setVM(
            (old) =>
              old && {
                ...old,
                job: {
                  status: 'failed',
                  format,
                  reason:
                    error instanceof CompanyRequestError && error.status === 403
                      ? 'denied'
                      : 'failed',
                  retryable: !leaseRequested.current,
                },
              },
          );
      } finally {
        busy.current = false;
      }
    },
    [
      request,
      locale,
      format,
      exportOriginal.begin,
      exportOriginal.resolved,
      exportOriginal.recovering,
      deliver,
    ],
  );
  useEffect(() => {
    if (!opened || busy.current || leaseRequested.current) return;
    let current = true;
    void artifactApi
      .readExportOptions(request, locale)
      .then((value) => {
        if (!current) return;
        setVM({ ...value, formats: value.formats.filter((row) => row.id === format) });
        setLoadError(false);
        if (exportOriginal.recovering && !recoveryStarted.current) {
          recoveryStarted.current = true;
          void exportOrRecover(true);
        }
      })
      .catch(() => {
        if (current) setLoadError(true);
      });
    return () => {
      current = false;
    };
  }, [opened, request, locale, format, exportOriginal.recovering, exportOrRecover]);
  return (
    <>
      {!embedded && (
        <button
          className="ap-saved-action"
          type="button"
          aria-expanded={opened}
          onClick={() => setOpened((old) => !old)}
        >
          {copy.exportTitle}
        </button>
      )}
      {opened &&
        (loadError ? (
          <ErrorState message={copy.exportUnavailable} />
        ) : vm ? (
          <ExportPanel
            vm={vm}
            onExport={() => void exportOrRecover(false)}
            onRetry={() => void exportOrRecover(true)}
          />
        ) : (
          <LoadingState />
        ))}
    </>
  );
}

/** One explicit gesture, retained copy original on retry; no GET creates an editor. */
function SavedEditButton({
  view,
  onOpen,
}: {
  view: SavedArtifactView;
  onOpen: (target: DraftCanvasTarget) => void;
}) {
  const copy = useNamespaceCopy(panelsCopy);
  const common = useNamespaceCopy(speakerCopy);
  const inspect = useNamespaceCopy(inspectCopy);
  const original = useRef<string | null>(null);
  const busy = useRef(false);
  const alive = useRef(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const edit = async () => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    setError(false);
    original.current ??= crypto.randomUUID();
    try {
      const result = await openNativeArtifactEditor(view, original.current);
      if (alive.current) onOpen({ kind: 'artifact-draft', id: result.draftId, tab: 'editor' });
    } catch {
      if (alive.current) setError(true);
    } finally {
      busy.current = false;
      if (alive.current) setPending(false);
    }
  };
  return (
    <>
      <button
        type="button"
        className="ap-saved-action"
        data-testid="artifact-edit"
        disabled={pending}
        onClick={() => void edit()}
      >
        {error ? copy.retry : common.edit}
      </button>
      {error && <p role="alert">{inspect.failed}</p>}
    </>
  );
}
